import React, { useEffect, useState } from 'react';

import { Box } from '@chakra-ui/react';
import { forEach, isEmpty } from 'lodash';
import { useSelector } from 'react-redux';

import { WithSpinner } from 'components/DataDisplay/Spinner/WithSpinner';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import { selectApp } from 'containers/App/selectors';
import { NoVulnerabilityScannerDeployed } from 'containers/Dashboard/utils/NoVulnerabilityScannerDeployed';
import { selectSharedState } from 'containers/SharedState/selectors';
import { VulnerabilityContextProvider } from 'containers/Visibility/Vulnerabilities/Components/hooks/context';
import { useVulnerabilities } from 'containers/Visibility/Vulnerabilities/Components/hooks/useVulnerabilities';
import { VulnerabilitySideNav } from 'containers/Visibility/Vulnerabilities/Components/VulnerabilitiesSideNav';

interface Props {
  useVulnerabilityV3?: boolean;
}

export const AllVulnerabilities: React.FC<Props> = ({
  useVulnerabilityV3 = false,
}) => {
  const { environments } = useSelector(selectApp);
  const {
    centralScanners,
    vulnerabilityScannerInfo,
    isVulnerabilityScannerDeployed,
  } = useSelector(selectSharedState);
  const { environmentId, accountId } = useAccessBoundary();
  const [environmentsObject, setEnvironmentsObject] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [showDashboard, setShowDashboard] = useState(true);

  useEffect(() => {
    if (!isEmpty(environments.data)) {
      let items = {};
      forEach(environments.data, o => (items[o.id] = o));
      setEnvironmentsObject(items);
    }
  }, [environments.data]);

  useEffect(() => {
    !!environmentId &&
      setAccounts(environmentsObject[environmentId]?.account_ids);
  }, [environmentId, environmentsObject]);

  useEffect(() => {
    if (useVulnerabilityV3) {
      setShowDashboard(true);
      return;
    }
    if (!isEmpty(centralScanners.data)) {
      setShowDashboard(true);
    } else {
      if (!!accountId)
        setShowDashboard(!!isVulnerabilityScannerDeployed[String(accountId)]);
      else
        setShowDashboard(
          !!(environmentId && isVulnerabilityScannerDeployed[environmentId]) ||
            Array.from(accounts ?? []).some(accountId =>
              Boolean(isVulnerabilityScannerDeployed[String(accountId)]),
            ),
        );
    }
  }, [
    accounts,
    accountId,
    environmentId,
    isVulnerabilityScannerDeployed,
    centralScanners.data,
    useVulnerabilityV3,
  ]);

  return (
    <VulnerabilityContextProvider
      value={useVulnerabilities(useVulnerabilityV3)}
    >
      <Box h="full">
        <WithSpinner
          loadStatus={{
            loading:
              environments.isLoading ||
              (!useVulnerabilityV3 &&
                (vulnerabilityScannerInfo.isLoading ||
                  centralScanners.isLoading)),
            loaded: useVulnerabilityV3 || vulnerabilityScannerInfo.isSuccess,
          }}
          spinnerSize="lg"
        >
          {showDashboard ? (
            <VulnerabilitySideNav useVulnerabilityV3={useVulnerabilityV3} />
          ) : (
            <NoVulnerabilityScannerDeployed />
          )}
        </WithSpinner>
      </Box>
    </VulnerabilityContextProvider>
  );
};
